/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { copyText } from './mavenUtils';
import { PROOF_ID_KIND } from './mavenParse';

/**
 * "Proof:" chips with the full ids behind the answer; a click copies the id. An id the check found to be an
 * eval run (not a request) is labelled "Eval run", so it is never mistaken for a request.
 */
const AnswerProof = ({ requestIds, kinds }) => {
  const [copiedId, setCopiedId] = useState(null);
  if (!requestIds.length) return null;
  const copy = (id) => {
    copyText(id);
    setCopiedId(id);
  };
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', mt: 1 }}>
      <Typography variant="caption" sx={{ mr: 0.5 }}>
        Proof:
      </Typography>
      {requestIds.map((id) => {
        const evalRun = kinds[id] === PROOF_ID_KIND.EVAL_RUN;
        const copyLabel = evalRun ? 'Copy eval run id' : 'Copy request id';
        return (
          <Tooltip key={id} title={copiedId === id ? 'Copied' : copyLabel}>
            <Chip
              size="small"
              variant="outlined"
              label={evalRun ? `Eval run ${id}` : id}
              onClick={() => copy(id)}
              onMouseLeave={() => setCopiedId(null)}
              sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11, maxWidth: '100%' }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

AnswerProof.propTypes = {
  requestIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  // id -> PROOF_ID_KIND, from the answer check; ids it has not checked yet show as requests.
  kinds: PropTypes.objectOf(PropTypes.string)
};

AnswerProof.defaultProps = { kinds: {} };

export default AnswerProof;
